import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { combineLatest, Observable, Subject } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';

import {
  AdvanceCertificationInstaller,
  AzzurroUserIdentityType,
  BaseCertificationInstaller,
  ServicePartnerCertification,
} from '../telemetry';
import {
  DissociateCertificationMutation,
  EnableCertificationMutation,
  DissociateCertificationInput,
  EnableCertificationInput,
} from '../telemetry/index.graph-ql';
import { azzurroUserIdentityQuery } from './query/azzurro-user-identity.gql';
import { AuthenticationService } from '../authentication/authentication.service';
import { clone, isNotNullOrUndefined, maybeSwitchMap } from '../utils';
import { GraphqlApiService } from '../graphql-api/graphql-api.service';

export type InstallerIdentity = ServicePartnerCertification | BaseCertificationInstaller | AdvanceCertificationInstaller;

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  readonly client$ = this.graphqlApiService.client$.pipe(isNotNullOrUndefined());

  readonly refresh$ = new Subject<void>();
  readonly identities$ = combineLatest([
    this.authenticationService.user$.pipe(map((user) => user?.email)),
    this.refresh$.pipe(startWith(null)),
  ]).pipe(
    maybeSwitchMap((_) => this.getAzzurroUserIdentities()),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  readonly is_ZCS_Operator$: Observable<Partial<AzzurroUserIdentityType> | undefined> = this.identities$.pipe(
    map((identities) => identities?.find((identity) => identity && (identity as any).__typename === 'Operator')),
  );

  readonly is_baseCertificationInstaller$: Observable<Partial<InstallerIdentity> | undefined> = this.identities$.pipe(
    map((identities) => identities?.find((identity) => identity && (identity as any).__typename === 'BaseCertificationInstaller')),
  );

  readonly is_advancedCertificationInstaller$: Observable<Partial<InstallerIdentity> | undefined> = this.identities$.pipe(
    map((identities) => identities?.find((identity) => identity && (identity as any).__typename === 'AdvanceCertificationInstaller')),
  );

  readonly is_servicePartner$: Observable<Partial<InstallerIdentity> | undefined> = this.identities$.pipe(
    map((identities) => identities?.find((identity) => identity && (identity as any).__typename  === 'ServicePartnerCertification')),
  );

  readonly has_somePermission$: Observable<boolean> = this.identities$.pipe(
    map((identities) => !!identities?.length),
  );

  readonly has_installerPermissions$: Observable<Partial<InstallerIdentity> | undefined> = combineLatest([
    this.is_baseCertificationInstaller$,
    this.is_advancedCertificationInstaller$,
    this.is_servicePartner$,
  ]).pipe(
    map(([baseCert, advanceCert, servicePartner]) => servicePartner ?? advanceCert ?? baseCert)
  );

  readonly is_ZCS_Operator_or_servicePartner$: Observable<boolean> = this.identities$.pipe(
    map((identities) => !!identities?.find((identity) => identity && (identity as any).__typename === 'Operator' || (identity as any).__typename === 'ServicePartnerCertification')),
  );

  readonly is_tester$: Observable<boolean> = this.identities$.pipe(
    map((identities) => !!identities?.some((identity) => (((identity as any)?.tags as string[]) ?? []).includes('tester'))),
  );

  readonly isFwManager$: Observable<boolean> = this.identities$.pipe(
    map((identities) => !!identities?.some((identity) => (((identity as any)?.tags as string[]) ?? []).includes('fw-manager'))),
  );

  readonly is_developer$: Observable<boolean> = this.identities$.pipe(
    map((identities) => !!identities?.some((identity) => (((identity as any)?.tags as string[]) ?? []).includes('developer'))),
  );

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly dissociateCertificationMutation: DissociateCertificationMutation,
    private readonly enableCertificationMutation: EnableCertificationMutation,
    private readonly graphqlApiService: GraphqlApiService,
  ) { }

  async dissociateCertification(emailCert: string, cod_installatore: number) {
    const input: DissociateCertificationInput = {
      emailCert,
      cod_installatore,
    };

    const query = gql`
      fragment successtype on SuccessType {
        success
      }
    `;

    const result = await this.dissociateCertificationMutation.execute(query, input);
    const success = result?.data?.dissociateCertification?.success ?? false;
    if (!success) {
      throw new Error('Error during certification dissociation');
    }
  }

  async enableCertification(emailCert: string, cod_installatore: number) {
    const input: EnableCertificationInput = {
      emailCert,
      cod_installatore,
    };

    const query = gql`
      fragment successtype on SuccessType {
        success
      }
    `;

    const result = await this.enableCertificationMutation.execute(query, input);
    const success = result?.data?.enableCertification?.success ?? false;
    if (!success) {
      throw new Error('Error during certification enabling');
    }
  }

  async getAzzurroUserIdentities() {
    const client = await this.client$.pipe(first()).toPromise();
    const query = azzurroUserIdentityQuery;
    const result = clone(await client.query<{ getAzzurroUserIdentities : Array<Partial<AzzurroUserIdentityType>> }>({ query, fetchPolicy: 'network-only' }));
    return result?.data?.getAzzurroUserIdentities;
  }
}
